import { ProMessagingService } from '../pro-messaging.service';
import { BehaviorSubject, lastValueFrom, Observable, of, Subscription } from 'rxjs';
import { MessagingGroupModel } from '../../api/models/messaging-group-model';
import { CollectionViewer, DataSource } from '@angular/cdk/collections';
import { MessageModel } from '../../api/models/message-model';
import { PhoneNumbersUtils } from '../../core/utilities/phone-numbers-helpers';
import { NotificationModel } from '../../api/models/notification-model';
import { inject, Injectable } from '@angular/core';
import { ContactLookupModel } from '../../api/models/contact-lookup-model';
import { GridSortModel } from '../../api/models/grid-sort-model';
import { AdvancedMessagingService as ApiAdvancedMessagingService } from '../../api/services/advanced-messaging.service';
import { LocalNotificationsService } from '../../core/services/local-notifications.service';
import { getPageRange } from '../../core/utilities/datasource-helper';
import { catchError, defaultIfEmpty } from 'rxjs/operators';

export interface FilterOptions {
  phoneNumber?: string;
  threadKey?: string;
  searchText?: string;
  sortType: 'sortByDateAndUnread' | 'sortByDate' | string;
  endDate?: Date;
  startDate?: Date;
}

@Injectable({ providedIn: 'root' })
export class ProPhoneNumberGroupV2Datasource implements DataSource<MessagingGroupModel | undefined> {
  private readonly TAG = '[ProPhoneNumberGroupV2Component]';
  private _length = 0;
  private _pageSize = 100;
  private _currentPage = 0;
  private _cachedData = Array.from<MessagingGroupModel>({ length: 0 });
  private _fetchedPages = new Set<number>();
  private _dataStream = new BehaviorSubject<(MessagingGroupModel | undefined)[]>(this._cachedData);
  private _subscription = new Subscription();
  private _newMessageSubscription = new Subscription();
  private _deleteMessageSubscription = new Subscription();
  private _newNotificationsSubscription = new Subscription();
  totalPinned = 0;
  totalGroups = 0;

  filterOptions: FilterOptions = { sortType: 'sortByDateAndUnread' };

  // Guards and coalescing
  private _lastFetchKey = '';
  private _filterDebounceTimer: any;
  private _refreshInFlight = false;

  messagingService = inject(ProMessagingService);
  private apiMessagingService = inject(ApiAdvancedMessagingService);
  private notificationsService = inject(LocalNotificationsService);

  private _fetchPageSubscriptions = new Map<number, Subscription>();

  connect(collectionViewer: CollectionViewer): Observable<(MessagingGroupModel | undefined)[]> {
    this._subscription = collectionViewer.viewChange.subscribe(async range => {
      console.log(`${this.TAG} range subscription`, range);

      if (range.end === 0 && range.start === 0) {
        return;
      }

      const pageRange = getPageRange(range, this._pageSize);
      this._currentPage = pageRange.end;

      for (let i = pageRange.start; i <= pageRange.end; i++) {
        await this._fetchPage(i);
      }
    });

    this._newMessageSubscription = this.messagingService.onNewMessageSubject.subscribe(async message => {
      await this._updateGroups(message);
    });

    this._deleteMessageSubscription = this.messagingService.onDeletedMessageSubject.subscribe(async message => {
      console.log(`${this.TAG} onDeleteMessageSubject`, message);
      if (!message) {
        return;
      }

      const group = this._cachedData.find(x => x.threadKey === message.threadKey);
      if (group && group.lastTime === message.time) {
        group.lastMessage = '';
        this._dataStream.next(this._cachedData);
      }
    });

    this._newNotificationsSubscription = this.messagingService.onNewNotificationsSubject.subscribe(
      async notifications => {
        await this._updateGroupsBadges(notifications);
      },
    );

    return this._dataStream;
  }

  disconnect(): void {
    console.log(`${this.TAG} disconnect`);
    this._subscription?.unsubscribe();
    this._newMessageSubscription?.unsubscribe();
    this._deleteMessageSubscription?.unsubscribe();
    this._newNotificationsSubscription?.unsubscribe();

    // Cancel all pending HTTP requests
    this._fetchPageSubscriptions.forEach(sub => sub.unsubscribe());
    this._fetchPageSubscriptions.clear();

    this._length = 0;
    this._fetchedPages = new Set<number>();

    this._cachedData = Array.from({ length: 0 });
    this._dataStream.next(this._cachedData);
  }

  async resetData(): Promise<void> {
    console.log(`${this.TAG} resetData`);
    await this._refreshData(0, true);
  }

  async updatePin(threadKey: string, pin: boolean): Promise<void> {
    console.log(`${this.TAG} updatePin`, threadKey, pin);
    const group = this._cachedData.find(x => x.threadKey === threadKey);
    if (group) {
      group.pin = pin;
      this.totalPinned = this._cachedData.filter(x => x?.pin).length;
      this.sortGroups();
      this._dataStream.next(this._cachedData);
    }
  }

  onFilterDataChanged(filterOptions: FilterOptions): void {
    console.log(`${this.TAG} onFilterDataChanged`, filterOptions);
    this.filterOptions = filterOptions;

    // Key on phone + search to avoid unnecessary refetches on sort/thread changes
    const normalizedPhone = (this.filterOptions.phoneNumber || '').trim();
    let normalizedSearch = (this.filterOptions.searchText || '').trim().toLowerCase();
    // check if normalizedSearch is possible to be a number
    const phoneNumber = PhoneNumbersUtils.phoneNumberToInternational(normalizedSearch);
    if (phoneNumber.startsWith('+')) {
      normalizedSearch = phoneNumber;
      this.filterOptions.searchText = phoneNumber;
    }
    const nextKey = `${normalizedPhone}|${normalizedSearch}`;

    if (this._lastFetchKey === nextKey) {
      console.log(`${this.TAG} onFilterDataChanged skipped (same key: ${nextKey})`);
      return;
    }

    // Cancel all pending HTTP requests when filter changes
    console.log(`${this.TAG} onFilterDataChanged - Cancelling all pending requests`);
    this._fetchPageSubscriptions.forEach(sub => sub.unsubscribe());
    this._fetchPageSubscriptions.clear();

    // Reset data if a phone number or search text changes
    if (this._lastFetchKey.indexOf(normalizedPhone) == -1) {
      this._fetchedPages = new Set<number>();
      this._length = 0;
      this._cachedData = Array.from({ length: 0 });

      this._dataStream.next(this._cachedData);
    }

    if (normalizedSearch && this._lastFetchKey.indexOf(`|${normalizedSearch}`) == -1) {
      this._cachedData = this._cachedData.filter(x => {
        let filtered = false;
        if (!x) {
          return false;
        }
        if (x.lastMessage) {
          filtered = x.lastMessage.toLowerCase().includes(normalizedSearch);
        }

        if (!filtered) {
          x.groupNumbers.forEach(groupNumber => {
            if (groupNumber.toLowerCase().includes(normalizedSearch)) {
              filtered = true;
            }
          });
        }

        if (!filtered) {
          x.contacts.forEach(contact => {
            if (contact.phoneNumber.toLowerCase().includes(normalizedSearch)) {
              filtered = true;
            }
            if (contact.name.toLowerCase().includes(normalizedSearch)) {
              filtered = true;
            }
          });
        }

        return filtered;
      });

      this._dataStream.next(this._cachedData);
    } else {
      this._dataStream.next(this._cachedData);
    }

    // Don't force select if we have a specific threadKey from URL - let the data load first
    // and selectGroup will be called after fetch completes
    if (!this.filterOptions.threadKey) {
      this.selectGroup(true);
    }

    this._lastFetchKey = nextKey;

    clearTimeout(this._filterDebounceTimer);
    this._filterDebounceTimer = setTimeout(() => {
      this._refreshData(0, true).then();
    }, 50);
  }

  onSortTypeChanged(sortType: 'sortByDateAndUnread' | 'sortByDate' | string) {
    console.log(`${this.TAG} onSortTypeChanged`, sortType);
    this.filterOptions.sortType = sortType;
    this.sortGroups();
    this._dataStream.next(this._cachedData);
  }

  onDateRangeChanged(startDate: Date, endDate: Date) {
    console.log(`${this.TAG} onDateRangeChanged`, startDate, endDate);
    this.filterOptions.startDate = startDate;
    this.filterOptions.endDate = endDate;
    this._refreshData(0, true).then();
  }

  onThreadKeyChanged(threadKey: string) {
    console.log(`${this.TAG} onThreadKeyChanged`, threadKey);
    this.filterOptions.threadKey = threadKey;

    if (this.filterOptions.threadKey === 'new') {
      return;
    }

    const selectedGroup = this._cachedData.find(x => x.threadKey === (this.filterOptions.threadKey || 'undefined'));

    if (!selectedGroup) {
      if (!this._refreshInFlight) {
        this._refreshData(this._currentPage, true).then();
      } else {
        console.log(`${this.TAG} onThreadKeyChanged refresh skipped due to in-flight refresh`);
      }
    } else {
      this.selectGroup();
    }
  }

  private async _refreshData(page: number, resetPages: boolean): Promise<void> {
    console.log(`${this.TAG} _refreshData`, page, resetPages);
    if (this._refreshInFlight) {
      console.log(`${this.TAG} _refreshData ignored; refresh already in flight`);
      return;
    }

    this._refreshInFlight = true;
    try {
      if (resetPages) {
        this._fetchedPages = new Set<number>();
        this._length = 0;
        this._cachedData = Array.from({ length: 0 });
      }

      await this._fetchPage(page);
      this.selectGroup();
    } finally {
      this._refreshInFlight = false;
    }
  }

  private selectGroup(forceSelect?: boolean) {
    if (this.filterOptions.threadKey === 'new') {
      return;
    }

    console.log(
      `${this.TAG} selectGroup`,
      this.filterOptions.threadKey,
      this.messagingService.isComposing,
      'forceSelect:',
      forceSelect,
    );
    let selectedGroup = this._cachedData.find(x => x.threadKey === (this.filterOptions.threadKey || 'undefined'));

    if (!this.messagingService.isComposing) {
      // If we have a specific threadKey from URL, only select if found
      if (this.filterOptions.threadKey && this.filterOptions.threadKey !== 'undefined') {
        if (selectedGroup) {
          this.messagingService.selectedGroup = selectedGroup;
          console.log(`${this.TAG} selectedGroup from URL threadKey`, selectedGroup, this.filterOptions.threadKey);
        } else {
          console.log(`${this.TAG} threadKey from URL not found yet, waiting for data:`, this.filterOptions.threadKey);
          this.messagingService.selectedGroup = this._cachedData[0];
        }
      } else {
        // No specific threadKey - select first thread as default (only if forceSelect or no selection exists)
        if (forceSelect || !this.messagingService.selectedGroupSig()?.threadKey) {
          selectedGroup = this._cachedData[0];
          if (selectedGroup) {
            this.messagingService.selectedGroup = selectedGroup;
            console.log(`${this.TAG} selectedGroup (default first)`, selectedGroup);
          } else {
            setTimeout(() => {
              this.messagingService.selectedGroup = undefined;
            });
            console.log(`${this.TAG} no groups to select`);
          }
        } else {
          console.log(`${this.TAG} keeping existing selection`);
        }
      }
    }
  }

  private async _updateGroupsBadges(notifications: NotificationModel[]) {
    console.log(`${this.TAG} updateGroupsBadges: ` + notifications.length);
    let notificationsChanged = false;
    for (const group of this._cachedData.filter(x => !!x)) {
      const notification = notifications.find(
        x =>
          (x.eventType === 'pro-sms' || x.eventType === 'whatsapp') &&
          x.tnTo === this.filterOptions.phoneNumber &&
          x.thread === group.threadKey,
      );

      const nextBadge = notification?.badge ?? 0;
      if (group.unread !== nextBadge) {
        notificationsChanged = true;
        group.unread = nextBadge;
      }
    }

    if (notificationsChanged) {
      this.sortGroups();
      this._dataStream.next(this._cachedData);
    }
  }

  private async _updateGroups(message: MessageModel) {
    if (!message) {
      return;
    }

    const mainPhoneNumber =
      message.direction === 'out'
        ? PhoneNumbersUtils.phoneNumberToNational(message.tnFrom)
        : PhoneNumbersUtils.phoneNumberToNational(message.tnTo);

    if (!this.filterOptions.phoneNumber || mainPhoneNumber !== this.filterOptions.phoneNumber) {
      return;
    }

    console.log(`${this.TAG} Update phone number groups`);

    const threadKey = message.threadKey;

    const existingGroup = this._cachedData.find(x => !!x && x.threadKey === threadKey);

    if (!existingGroup) {
      console.log(`${this.TAG} New group: `, message);
      const newGroup = this.createGroupModel(message);
      this._cachedData.unshift(newGroup);
      this._length = this._cachedData.length;
      this.sortGroups();
      this._dataStream.next(this._cachedData);
    } else {
      let messageText = message.messageText;
      if (!messageText && message.media.length > 0) {
        messageText = message.direction === 'out' ? 'Media sent' : 'Media received';
      }
      existingGroup.threadKey = threadKey;
      existingGroup.primaryNumber = mainPhoneNumber;
      existingGroup.lastTime = message.time;
      existingGroup.lastMessage = messageText;
      existingGroup.groupNumbers = message.groupNumbers;
      existingGroup.broadcastId = message.broadcastId;

      if (message.wabaConvId) {
        existingGroup.wabaConvId = message.wabaConvId;
      }
      if (message.wabaConvExp) {
        existingGroup.wabaConvExp = message.wabaConvExp;
      }

      this.sortGroups();
      this._dataStream.next(this._cachedData);
    }
  }

  private _fetchPage(page: number): Promise<void> {
    console.log(`${this.TAG} _fetchPage ---- Datasource page: ` + page);

    return new Promise<void>((resolve, reject) => {
      if (this._fetchedPages.has(page)) {
        resolve();
        return;
      }

      // Cancel any existing fetch for this page
      if (this._fetchPageSubscriptions.has(page)) {
        console.log(`${this.TAG} _fetchPage ---- Cancelling existing fetch for page: ` + page);
        this._fetchPageSubscriptions.get(page)?.unsubscribe();
        this._fetchPageSubscriptions.delete(page);
      }

      this._fetchedPages.add(page);

      if (!this.filterOptions.phoneNumber) {
        this._cachedData = Array.from<MessagingGroupModel>({ length: 0 });
        this._dataStream.next(this._cachedData);
        resolve();
        return;
      }

      const request = {
        phoneNumber: this.filterOptions.phoneNumber,
        searchText: this.filterOptions.searchText,
        limit: this._pageSize,
        skip: this._pageSize * page,
        forceUpdate: false,
        startDate: this.filterOptions.startDate,
        endDate: this.filterOptions.endDate,
      };

      console.log(`${this.TAG} _fetchPage ---- get messaging threads page:` + page, request);

      const subscription = this.apiMessagingService
        .proMessagingGetMessagingGroupsV2({
          phoneNumber: request.phoneNumber,
          body: {
            searchText: request.searchText,
            sort: undefined,
            skip: request.skip || 0,
            limit: request.limit || 100,
            phoneNumber: request.phoneNumber,
            startDate: this.filterOptions.startDate?.toISOString(),
            endDate: this.filterOptions.endDate?.toISOString(),
          },
        })
        .pipe(
          catchError(error => {
            console.error(`${this.TAG} Error getting messaging groups:`, error);
            return of({ data: [], total: 0 });
          }),
          defaultIfEmpty({ data: [], total: 0 }),
        )
        .subscribe({
          next: async response => {
            const groups = response.data || [];
            this.totalGroups = response.total || 0;

            console.log(`${this.TAG} _fetchPage ---- after get messaging threads total:` + groups.length, request);

            if (this._length === 0) {
              this._length = this.totalGroups;
              this._cachedData = Array.from<MessagingGroupModel>({ length: this._length });
            }

            this._length = this.totalGroups;
            this._cachedData.splice(request.skip, groups.length, ...groups);

            this.totalPinned = this._cachedData.filter(x => x?.pin).length;

            this.sortGroups();

            await this._updateGroupsBadges(this.notificationsService.latestNotifications.getValue());

            this._dataStream.next(this._cachedData);

            this._fetchPageSubscriptions.delete(page);
            resolve();
          },
          error: err => {
            console.error(`${this.TAG} _fetchPage error:`, err);
            this._fetchPageSubscriptions.delete(page);
            reject(err);
          },
        });

      this._fetchPageSubscriptions.set(page, subscription);
    });
  }

  public async queryPhoneNumberThreads(options: {
    phoneNumber: string;
    skip?: number;
    limit?: number;
    searchText?: string;
    sort?: Array<GridSortModel>;
    threadKey?: string;
    forceUpdate: boolean;
  }): Promise<MessagingGroupModel[]> {
    console.log('update queryPhoneNumberGroups:', options.phoneNumber);

    if (!options.phoneNumber) {
      return [];
    }

    const response = await lastValueFrom(
      this.apiMessagingService
        .proMessagingGetMessagingGroupsV2({
          phoneNumber: options.phoneNumber,
          body: {
            searchText: options.searchText,
            sort: options.sort,
            skip: options.skip || 0,
            limit: options.limit || 100,
            phoneNumber: options.phoneNumber,
          },
        })
        .pipe(
          catchError(error => {
            console.error('Error getting messaging groups:', error);
            return of({ data: [], total: 0 });
          }),
          defaultIfEmpty({ data: [], total: 0 }),
        ),
    );
    const allGroups = response.data;
    this.totalGroups = response.total;

    return allGroups;
  }

  sortGroups() {
    console.log(`${this.TAG} sortGroups ${this.filterOptions.sortType}`);
    this._cachedData.sort((a, b) => {
      // Convert lastTime to Date objects
      const aTime = new Date(a.lastTime || '');
      const bTime = new Date(b.lastTime || '');

      // Determine pinned status (1 if pinned, 0 if not)
      const aPinned = a.pin ? 1 : 0;
      const bPinned = b.pin ? 1 : 0;

      // Determine unread status (1 if unread, 0 if not)
      const aUnread = a.unread > 0 ? 1 : 0;
      const bUnread = b.unread > 0 ? 1 : 0;

      // If pinned statuses are different, prioritize pinned items
      if (aPinned !== bPinned) {
        return bPinned - aPinned; // Higher priority for pinned
      }

      // Sorting by "sortByDateAndUnread" criteria
      if (this.filterOptions.sortType === 'sortByDateAndUnread') {
        // If unread statuses are different, prioritize unread items
        if (aUnread !== bUnread) {
          return bUnread - aUnread; // Higher priority for unread
        }
      }

      // Default sorting by date (newest first)
      return bTime.getTime() - aTime.getTime();
    });
  }

  private createGroupModel(message: MessageModel): MessagingGroupModel {
    const primaryNumber = PhoneNumbersUtils.phoneNumberToInternational(
      message.direction === 'in' ? message.tnTo : message.tnFrom,
    );
    let messageText = message.messageText;
    if (!messageText && message.media.length > 0) {
      messageText = message.direction === 'out' ? 'Media sent' : 'Media received';
    }

    return {
      threadKey: message.threadKey,
      primaryNumber: primaryNumber,
      lastTime: message.time,
      lastMessage: messageText,
      groupNumbers: message.groupNumbers,
      broadcastId: message.broadcastId,
      unread: message.direction === 'in' ? 1 : 0,
      pin: false,
      groupKey: message.groupKey,
      isGroup: message.groupNumbers.length > 2,
      isBroadcast: !!message.broadcastId,
      wabaConvId: message.wabaConvId,
      wabaConvExp: message.wabaConvExp,
      updatedOn: message.updatedOn,
      rowNumber: -99,
      contacts: message.groupNumbers
        .filter(x => x != primaryNumber)
        .map(x => {
          return {
            id: '0',
            isOptedIn: true,
            communicationType: 0,
            phoneNumber: x,
            phoneType: 'Contacts',
          } as ContactLookupModel;
        }),
    };
  }
}
